import React, { useState, useEffect } from 'react';
import { FaUpload, FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const PersonalDetails = ({ formData, onSubmit, loading, updateFormData }) => {
    const [localData, setLocalData] = useState({
        ...formData,
        birth_location: formData.birth_location || '153', // Default country ID 153 (Nepal)
        birth_state: formData.birth_state || '2', // Default state ID 2
        birth_city: formData.birth_city || '19' // Default city ID 19
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [dropdowns, setDropdowns] = useState({
        caste: [],
        gotra: [],
        rashi: [],
        countries: [],
        children_status: []
    });
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Filter children status to remove "Not Applicable"
    const getFilteredChildrenStatus = (statusList) => {
        if (!statusList) return [];
        return statusList.filter(item => 
            item.status_name !== 'Not Applicable' && 
            item.name !== 'Not Applicable'
        );
    };

    // Fetch dropdown data
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const response = await axios.get('https://app.bihebari.com.np/api/dropdowns');
                if (response.data.success) {
                    setDropdowns({
                        ...response.data.data,
                        children_status: getFilteredChildrenStatus(response.data.data.children_status)
                    });
                    console.log('Dropdowns loaded:', response.data.data);
                }
            } catch (error) {
                console.error('Error fetching dropdowns:', error);
                toast.error('Error loading form data');
            } finally {
                setLoadingDropdowns(false);
            }
        };
        fetchDropdowns();
    }, []);

    // Fetch states when country changes (including initial load)
    useEffect(() => {
        const fetchStates = async () => {
            if (localData.birth_location) {
                setLoadingStates(true);
                try {
                    const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${localData.birth_location}`);
                    if (response.data.success) {
                        setStates(response.data.data);
                        
                        // On initial load, set default state if not already set
                        if (isInitialLoad && !formData.birth_state && localData.birth_location === '153') {
                            setLocalData(prev => ({ ...prev, birth_state: '2' }));
                            updateFormData({ ...formData, birth_state: '2' });
                        }
                        
                        // Reset state and city only if country changes (not on initial load)
                        if (!isInitialLoad) {
                            setLocalData(prev => ({ ...prev, birth_state: '', birth_city: '' }));
                            updateFormData({ ...formData, birth_state: '', birth_city: '' });
                        }
                    } else {
                        setStates([]);
                    }
                } catch (error) {
                    console.error('Error fetching states:', error);
                    setStates([]);
                } finally {
                    setLoadingStates(false);
                }
            } else {
                setStates([]);
                setCities([]);
            }
        };
        fetchStates();
    }, [localData.birth_location]);

    // Fetch cities when state changes (including initial load)
    useEffect(() => {
        const fetchCities = async () => {
            if (localData.birth_state) {
                setLoadingCities(true);
                try {
                    const response = await axios.get(`https://app.bihebari.com.np/api/locations/cities/${localData.birth_state}`);
                    if (response.data.success) {
                        setCities(response.data.data);
                        
                        // On initial load, set default city if not already set
                        if (isInitialLoad && !formData.birth_city && localData.birth_state === '2') {
                            setLocalData(prev => ({ ...prev, birth_city: '19' }));
                            updateFormData({ ...formData, birth_city: '19' });
                            setIsInitialLoad(false); // Mark initial load as complete
                        }
                        
                        // Reset city only if state changes (not on initial load)
                        if (!isInitialLoad) {
                            setLocalData(prev => ({ ...prev, birth_city: '' }));
                            updateFormData({ ...formData, birth_city: '' });
                        }
                    } else {
                        setCities([]);
                    }
                } catch (error) {
                    console.error('Error fetching cities:', error);
                    setCities([]);
                } finally {
                    setLoadingCities(false);
                }
            } else {
                setCities([]);
            }
        };
        fetchCities();
    }, [localData.birth_state]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        
        if (type === 'file') {
            const file = files[0];
            if (file) {
                if (file.size > 5 * 1024 * 1024) {
                    setErrors({ ...errors, image: 'File size must be less than 5MB' });
                    return;
                }
                
                const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
                if (!allowedTypes.includes(file.type)) {
                    setErrors({ ...errors, image: 'Only JPEG, PNG, GIF images are allowed' });
                    return;
                }
                
                setLocalData({ ...localData, [name]: file });
                updateFormData({ ...formData, [name]: file });
                setErrors({ ...errors, image: null });
                
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            }
        } else {
            setLocalData({ ...localData, [name]: value });
            updateFormData({ ...formData, [name]: value });
            
            // Mark field as touched
            setTouched({ ...touched, [name]: true });
            
            // Clear dependent fields when marital status changes
            if (name === 'marital' && value === 'unmarried') {
                setLocalData(prev => ({ ...prev, children_status: '', children: '' }));
                updateFormData({ ...formData, children_status: '', children: '' });
            }
            
            // Clear total children when children status is "No Children"
            if (name === 'children_status' && value === 'No Children') {
                setLocalData(prev => ({ ...prev, children: '0' }));
                updateFormData({ ...formData, children: '0' });
            }
            
            // Clear state and city when country changes
            if (name === 'birth_location') {
                setLocalData(prev => ({ ...prev, birth_state: '', birth_city: '' }));
                updateFormData({ ...formData, birth_state: '', birth_city: '' });
            }
            
            // Clear city when state changes
            if (name === 'birth_state') {
                setLocalData(prev => ({ ...prev, birth_city: '' }));
                updateFormData({ ...formData, birth_city: '' });
            }
            
            if (errors[name]) {
                setErrors({ ...errors, [name]: null });
            }
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        validateField(name, localData[name]);
    };

    const validateField = (fieldName, value) => {
        let error = '';
        
        switch (fieldName) {
            case 'name':
                if (!value || value.trim() === '') {
                    error = 'Full name is required';
                } else if (value.length < 2) {
                    error = 'Name must be at least 2 characters';
                } else if (value.length > 100) {
                    error = 'Name must be less than 100 characters';
                }
                break;
            case 'gender':
                if (!value || value === '') {
                    error = 'Gender is required';
                }
                break;
            case 'dob':
                if (!value) {
                    error = 'Date of birth is required';
                } else if (!validateAge(value)) {
                    error = 'Age must be 20 years or above';
                }
                break;
            case 'caste':
                if (!value || value === '') {
                    error = 'Caste is required';
                }
                break;
            case 'horscope':
                if (!value || value === '') {
                    error = 'Horoscope/Rashi is required';
                }
                break;
            case 'birth_location':
                if (!value || value === '') {
                    error = 'Birth country is required';
                }
                break;
            case 'birth_state':
                if (!value || value === '') {
                    error = 'Birth state is required';
                }
                break;
            case 'birth_city':
                if (!value || value === '') {
                    error = 'Birth city is required';
                }
                break;
            case 'gotra':
                if (!value || value === '') {
                    error = 'Gotra is required';
                }
                break;
            case 'marital':
                if (!value || value === '') {
                    error = 'Marital status is required';
                }
                break;
            case 'children_status':
                if (!value || value === '') {
                    error = 'Children status is required';
                }
                break;
            case 'children':
                if (!value || value === '') {
                    error = 'Number of children is required';
                } else if (parseInt(value) < 0) {
                    error = 'Number of children cannot be negative';
                }
                break;
            case 'eat_status':
                if (!value || value === '') {
                    error = 'Food preference is required';
                }
                break;
            case 'moms_surname':
                if (!value || value.trim() === '') {
                    error = "Mother's surname is required";
                }
                break;
            case 'about_me':
                if (!value || value.trim() === '') {
                    error = 'About me is required';
                } else if (value.length < 10) {
                    error = 'Please provide at least 10 characters';
                }
                break;
            default:
                break;
        }
        
        setErrors(prev => ({ ...prev, [fieldName]: error }));
        return error === '';
    };

    const validateAge = (dob) => {
        if (!dob) return false;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age >= 20;
    };

    const validateForm = () => {
        const fieldsToValidate = [
            'name', 'gender', 'dob', 'caste', 'horscope', 
            'birth_location', 'birth_state', 'birth_city', 'gotra', 
            'marital', 'eat_status', 'moms_surname', 'about_me'
        ];
        
        let isValid = true;
        const newErrors = {};
        
        fieldsToValidate.forEach(field => {
            const value = localData[field];
            let error = '';
            
            switch (field) {
                case 'name':
                    if (!value || value.trim() === '') error = 'Full name is required';
                    break;
                case 'gender':
                    if (!value || value === '') error = 'Gender is required';
                    break;
                case 'dob':
                    if (!value) {
                        error = 'Date of birth is required';
                    } else if (!validateAge(value)) {
                        error = 'Age must be 20 years or above';
                    }
                    break;
                case 'caste':
                    if (!value || value === '') error = 'Caste is required';
                    break;
                case 'horscope':
                    if (!value || value === '') error = 'Horoscope/Rashi is required';
                    break;
                case 'birth_location':
                    if (!value || value === '') error = 'Birth country is required';
                    break;
                case 'birth_state':
                    if (!value || value === '') error = 'Birth state is required';
                    break;
                case 'birth_city':
                    if (!value || value === '') error = 'Birth city is required';
                    break;
                case 'gotra':
                    if (!value || value === '') error = 'Gotra is required';
                    break;
                case 'marital':
                    if (!value || value === '') error = 'Marital status is required';
                    break;
                case 'eat_status':
                    if (!value || value === '') error = 'Food preference is required';
                    break;
                case 'moms_surname':
                    if (!value || value.trim() === '') error = "Mother's surname is required";
                    break;
                case 'about_me':
                    if (!value || value.trim() === '') {
                        error = 'About me is required';
                    } else if (value.length < 10) {
                        error = 'Please provide at least 10 characters';
                    }
                    break;
                default:
                    break;
            }
            
            if (error) {
                isValid = false;
                newErrors[field] = error;
            }
        });
        
        // Conditional validation for children status and total children
        if (localData.marital !== 'unmarried') {
            if (!localData.children_status) {
                isValid = false;
                newErrors.children_status = 'Children status is required';
            }
            
            if (localData.children_status && localData.children_status !== 'No Children') {
                if (!localData.children || localData.children === '') {
                    isValid = false;
                    newErrors.children = 'Please enter number of children';
                } else if (parseInt(localData.children) < 0) {
                    isValid = false;
                    newErrors.children = 'Number of children cannot be negative';
                }
            }
        }
        
        setErrors(newErrors);
        
        // Mark all fields as touched
        const allTouched = {};
        [...fieldsToValidate, 'children_status', 'children'].forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);
        
        return isValid;
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(localData);
        } else {
            toast.error('Please fill all required fields correctly');
        }
    };

    // Determine if Children Status should be shown
    const showChildrenStatus = localData.marital && localData.marital !== 'unmarried';
    
    // Determine if Total Children field should be shown
    const showTotalChildren = showChildrenStatus && 
        localData.children_status && 
        localData.children_status !== 'No Children';

    if (loadingDropdowns) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="inline-block w-12 h-12 border-t-2 border-indigo-600 rounded-full animate-spin"></div>
                    <p className="mt-2 text-gray-600">Loading form data...</p>
                </div>
            </div>
        );
    }
    
    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center space-x-2 mb-6">
                <FaUser className="text-indigo-600 text-xl" />
                <h2 className="text-2xl font-bold text-gray-800">Personal Details</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                    <label className="form-label">
                        Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={localData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.name && touched.name ? 'input-error' : ''}`}
                        placeholder="Enter your full name"
                    />
                    {errors.name && touched.name && <p className="error-message">{errors.name}</p>}
                </div>
                
                {/* Gender */}
                <div>
                    <label className="form-label">
                        Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="gender"
                        value={localData.gender}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.gender && touched.gender ? 'input-error' : ''}`}
                    >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                    </select>
                    {errors.gender && touched.gender && <p className="error-message">{errors.gender}</p>}
                </div>
                
                {/* Date of Birth */}
                <div>
                    <label className="form-label">
                        Date of Birth <span className="text-red-500">*</span>
                        <span className="text-xs text-gray-500 ml-2">(Must be 20 years or above)</span>
                    </label>
                    <input
                        type="date"
                        name="dob"
                        value={localData.dob}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.dob && touched.dob ? 'input-error' : ''}`}
                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 20)).toISOString().split('T')[0]}
                    />
                    {errors.dob && touched.dob && <p className="error-message">{errors.dob}</p>}
                </div>
                
                {/* Birth Time - Optional */}
                <div>
                    <label className="form-label">Birth Time</label>
                    <input
                        type="time"
                        name="birth_time"
                        value={localData.birth_time}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>
                
                {/* Caste Dropdown */}
                <div>
                    <label className="form-label">
                        Caste <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="caste"
                        value={localData.caste}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.caste && touched.caste ? 'input-error' : ''}`}
                    >
                        <option value="">Select Caste</option>
                        {dropdowns.caste && dropdowns.caste.map((item) => (
                            <option key={item.id} value={item.caste_name || item.name}>
                                {item.caste_name || item.name}
                            </option>
                        ))}
                    </select>
                    {errors.caste && touched.caste && <p className="error-message">{errors.caste}</p>}
                </div>
                
                {/* Horoscope/Rashi Dropdown */}
                <div>
                    <label className="form-label">
                        Horoscope / Rashi <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="horscope"
                        value={localData.horscope}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.horscope && touched.horscope ? 'input-error' : ''}`}
                    >
                        <option value="">Select Rashi</option>
                        {dropdowns.rashi && dropdowns.rashi.map((item) => (
                            <option key={item.id} value={item.rashi_name || item.name}>
                                {item.rashi_name || item.name}
                            </option>
                        ))}
                    </select>
                    {errors.horscope && touched.horscope && <p className="error-message">{errors.horscope}</p>}
                </div>
                
                {/* Birth Location - Country Dropdown */}
                <div>
                    <label className="form-label">
                        Birth Country <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="birth_location"
                        value={localData.birth_location}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.birth_location && touched.birth_location ? 'input-error' : ''}`}
                    >
                        <option value="">Select Country</option>
                        {dropdowns.countries && dropdowns.countries.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                    {errors.birth_location && touched.birth_location && <p className="error-message">{errors.birth_location}</p>}
                </div>
                
                {/* Birth State Dropdown */}
                <div>
                    <label className="form-label">
                        Birth State <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="birth_state"
                        value={localData.birth_state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.birth_state && touched.birth_state ? 'input-error' : ''}`}
                        disabled={!localData.birth_location || loadingStates}
                    >
                        <option value="">Select State</option>
                        {states.map((state) => (
                            <option key={state.id} value={state.id}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                    {errors.birth_state && touched.birth_state && <p className="error-message">{errors.birth_state}</p>}
                    {loadingStates && <p className="text-xs text-gray-500 mt-1">Loading states...</p>}
                    {!localData.birth_location && <p className="text-xs text-gray-500 mt-1">Please select country first</p>}
                </div>
                
                {/* Birth City Dropdown */}
                <div>
                    <label className="form-label">
                        Birth City <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="birth_city"
                        value={localData.birth_city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.birth_city && touched.birth_city ? 'input-error' : ''}`}
                        disabled={!localData.birth_state || loadingCities}
                    >
                        <option value="">Select City</option>
                        {cities.map((city) => (
                            <option key={city.id} value={city.id}>
                                {city.name}
                            </option>
                        ))}
                    </select>
                    {errors.birth_city && touched.birth_city && <p className="error-message">{errors.birth_city}</p>}
                    {loadingCities && <p className="text-xs text-gray-500 mt-1">Loading cities...</p>}
                    {!localData.birth_state && localData.birth_location && <p className="text-xs text-gray-500 mt-1">Please select state first</p>}
                </div>
                
                {/* Gotra Dropdown */}
                <div>
                    <label className="form-label">
                        Gotra <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="gotra"
                        value={localData.gotra}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.gotra && touched.gotra ? 'input-error' : ''}`}
                    >
                        <option value="">Select Gotra</option>
                        {dropdowns.gotra && dropdowns.gotra.map((item) => (
                            <option key={item.id} value={item.gotra_name || item.name}>
                                {item.gotra_name || item.name}
                            </option>
                        ))}
                    </select>
                    {errors.gotra && touched.gotra && <p className="error-message">{errors.gotra}</p>}
                </div>
                
                {/* Marital Status */}
                <div>
                    <label className="form-label">
                        Marital Status <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="marital"
                        value={localData.marital}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.marital && touched.marital ? 'input-error' : ''}`}
                    >
                        <option value="">Select Status</option>
                        <option value="unmarried">Unmarried</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widow/Widower</option>
                    </select>
                    {errors.marital && touched.marital && <p className="error-message">{errors.marital}</p>}
                </div>
                
                {/* Children Status - Conditional */}
                {showChildrenStatus && (
                    <div>
                        <label className="form-label">
                            Children Status <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="children_status"
                            value={localData.children_status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`form-input ${errors.children_status && touched.children_status ? 'input-error' : ''}`}
                        >
                            <option value="">Select Children Status</option>
                            {dropdowns.children_status && dropdowns.children_status.map((item) => (
                                <option key={item.id} value={item.status_name || item.name}>
                                    {item.status_name || item.name}
                                </option>
                            ))}
                        </select>
                        {errors.children_status && touched.children_status && <p className="error-message">{errors.children_status}</p>}
                    </div>
                )}
                
                {/* Total Children - Conditional */}
                {showTotalChildren && (
                    <div>
                        <label className="form-label">
                            Total Children <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            name="children"
                            value={localData.children}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            min="0"
                            className={`form-input ${errors.children && touched.children ? 'input-error' : ''}`}
                            placeholder="Enter number of children"
                        />
                        {errors.children && touched.children && <p className="error-message">{errors.children}</p>}
                        <p className="text-xs text-gray-500 mt-1">Enter the total number of children</p>
                    </div>
                )}
                
                {/* Food Preference */}
                <div>
                    <label className="form-label">
                        Food Preference <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="eat_status"
                        value={localData.eat_status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.eat_status && touched.eat_status ? 'input-error' : ''}`}
                    >
                        <option value="">Select Preference</option>
                        <option value="vegetarian">Vegetarian</option>
                        <option value="non-vegetarian">Non-Vegetarian</option>
                    </select>
                    {errors.eat_status && touched.eat_status && <p className="error-message">{errors.eat_status}</p>}
                </div>
                
                {/* Mother's Surname */}
                <div>
                    <label className="form-label">
                        Mother's Surname <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="moms_surname"
                        value={localData.moms_surname}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.moms_surname && touched.moms_surname ? 'input-error' : ''}`}
                        placeholder="Enter mother's surname"
                    />
                    {errors.moms_surname && touched.moms_surname && <p className="error-message">{errors.moms_surname}</p>}
                </div>
                
                {/* Photo Upload - Optional */}
                <div className="md:col-span-2">
                    <label className="form-label">Profile Photo (Optional)</label>
                    <div className="flex items-center space-x-4">
                        {imagePreview && (
                            <img 
                                src={imagePreview} 
                                alt="Preview" 
                                className="w-24 h-24 rounded-full object-cover border-2 border-indigo-300"
                            />
                        )}
                        <div className="flex-1">
                            <label className="flex items-center space-x-2 cursor-pointer bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition">
                                <FaUpload />
                                <span>Choose Photo</span>
                                <input
                                    type="file"
                                    name="image"
                                    onChange={handleChange}
                                    accept="image/*"
                                    className="hidden"
                                />
                            </label>
                            <p className="text-xs text-gray-500 mt-1">Max size: 5MB. Allowed: JPEG, PNG, GIF</p>
                        </div>
                    </div>
                    {errors.image && <p className="error-message">{errors.image}</p>}
                </div>
                
                {/* About Me */}
                <div className="md:col-span-2">
                    <label className="form-label">
                        About Me <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="about_me"
                        value={localData.about_me}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        rows="4"
                        className={`form-input ${errors.about_me && touched.about_me ? 'input-error' : ''}`}
                        placeholder="Tell us about yourself..."
                    ></textarea>
                    {errors.about_me && touched.about_me && <p className="error-message">{errors.about_me}</p>}
                    <p className="text-xs text-gray-500 mt-1">Minimum 10 characters</p>
                </div>
            </div>
            
            {/* Hidden amount field - default 5000 */}
            <input type="hidden" name="amount" value="10" />
            
            <div className="flex justify-end pt-4">
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                >
                    {loading ? (
                        <>
                            <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                            <span>Saving...</span>
                        </>
                    ) : (
                        <>
                            <span>Next: Appearance Details</span>
                            <span>→</span>
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default PersonalDetails;